<template>
  <a-modal
    :title="id ? `Редактировать строку` : `Добавить строку`"
    v-model="isVisible"
    class="row-modal"
  >
    <a-form>
      <p v-if="!columns.length">
        Чтобы добавить строку, сначала нужно добавить колонку
      </p>
      <a-form-item
        v-for="column in processedColumns"
        :key="column.name"
        class="form-item"
      >
        <span slot="label">
          <a-tooltip v-if="column.restrictors.length">
            <div slot="title">
              Ограничители:
              <ul class="restrictors">
                <li
                  v-for="restrictor in column.restrictors"
                  :class="{ li_red: !restrictor.validated }"
                  :key="restrictor.name"
                >
                  {{ restrictor.label }} {{ getRestrictorParams(restrictor) }}
                </li>
              </ul>
            </div>
            <a-icon
              :class="{
                icon_red: column.restrictors.some((r) => !r.validated),
              }"
              type="warning"
            ></a-icon>
          </a-tooltip>
          {{ column.gui_name || column.name }}
        </span>

        <a-input
          v-if="!column.decorator_name"
          v-model="params[column.name]"
          @input="validate"
        ></a-input>
        <a-date-picker
          class="datepicker"
          v-else-if="
            column.decorator_name === 'datepicker' && params[column.name]
          "
          :value="getMoment(params[column.name])"
          @change="handleDatepicker($event, column.name)"
          :showTime="{ format: 'HH:mm', defaultValue: getMoment(75600) }"
          format="DD.MM.YYYY HH:mm"
          allowClear
          :locale="dateLocale"
        ></a-date-picker>
        <a-date-picker
          class="datepicker"
          v-else-if="column.decorator_name === 'datepicker'"
          @change="handleDatepicker($event, column.name)"
          :showTime="{ format: 'HH:mm', defaultValue: getMoment(75600) }"
          format="DD.MM.YYYY HH:mm"
          allowClear
          :locale="dateLocale"
        ></a-date-picker>
        <a-select
          v-else-if="column.decorator_name === 'boolean'"
          placeholder="Выберите значение"
          v-model="params[column.name]"
          allowClear
          @change="validate"
        >
          <a-select-option value="1">Да</a-select-option>
          <a-select-option value="0">Нет</a-select-option>
        </a-select>
        <a-checkbox-group
          v-else-if="column.decorator_name === 'checkbox'"
          :options="getCheckboxOptions(column)"
          :value="(params[column.name] || '').split(', ')"
          @change="
            params[column.name] = $event.sort().join(', ');
            validate();
          "
        >
        </a-checkbox-group>
        <a-radio-group
          v-else-if="column.decorator_name === 'radio'"
          :options="getRadioOptions(column)"
          v-model="params[column.name]"
          @change="validate"
        >
        </a-radio-group>
        <a-textarea
          v-else-if="column.decorator_name === 'textarea'"
          v-model="params[column.name]"
          @change="validate"
          :autoSize="{ minRows: 3 }"
        ></a-textarea>
        <editor
          @change="validate"
          v-else-if="column.decorator_name === 'wysiwyg'"
          :value="params[column.name] || ''"
          @input="
            params[column.name] = $event;
            validate();
          "
          :init="{
            height: 300,
            menubar: false,
            language: 'ru',
            language_url: `${baseUrl}tinymce-ru.js`,
            content_style: 'body {font-family: \'Roboto\', sans-serif;}',
            inline_styles: true,
            style_formats: formats,
            plugins: 'link code lists table image media wordcount',
            toolbar: [
              'undo redo | styleselect | bold italic link numlist bullist table',
              'image media code',
            ],
          }"
        />
        <div v-else-if="column.decorator_name === 'entity'" class="find-entity">
          <a-input v-model="params[column.name]" @change="validate"></a-input>
          <a-button
            class="find-entity__button"
            :disabled="column.name.split('_')[1] !== 'id'"
            @click="
              findEntity.isVisible = true;
              findEntity.multiple = false;
              findEntity.entity = column.name.split('_')[0];
              findEntity.param = column.name;
            "
          >
            Найти
          </a-button>
        </div>
        <div
          v-else-if="column.decorator_name === 'entities'"
          class="find-entity"
        >
          <a-input v-model="params[column.name]" @change="validate"></a-input>
          <a-button
            class="find-entity__button"
            :disabled="column.name.split('_')[1] !== 'ids'"
            @click="
              findEntity.isVisible = true;
              findEntity.multiple = true;
              findEntity.result =
                typeof params[column.name] === 'string' && params[column.name]
                  ? params[column.name].split(',')
                  : Array.isArray(params[column.name])
                  ? params[column.name]
                  : [];
              findEntity.entity = column.name.split('_')[0];
              findEntity.param = column.name;
            "
          >
            Найти
          </a-button>
        </div>
        <div
          v-else-if="column.decorator_name === 'image'"
          class="upload-image"
          :class="{ 'upload-image_pending': isRequestPending(column.name) }"
        >
          <a-upload-dragger
            accept="image/*"
            :beforeUpload="handleImage(column.name)"
            :showUploadList="false"
          >
            <a-spin class="upload-image__loader"></a-spin>
            <p class="ant-upload-drag-icon">
              <a-icon type="inbox" />
            </p>
            <p class="ant-upload-text">
              Кликните или перетащите сюда изображение для загрузки
            </p>
          </a-upload-dragger>
          <a-button
            block
            type="danger"
            v-show="params[column.name]"
            @click="params[column.name] = null"
          >
            Удалить изображение
          </a-button>
          <img
            :ref="column.name + '-image'"
            class="upload-image__preview"
            v-show="params[column.name]"
            :src="params[column.name]"
          />
        </div>
        <div
          v-else-if="column.decorator_name === 'file'"
          class="upload-file"
          :class="{ 'upload-file_pending': isRequestPending(column.name) }"
        >
          <a-upload-dragger
            :beforeUpload="handleFile(column)"
            :showUploadList="false"
          >
            <a-spin class="upload-file__loader"></a-spin>
            <p class="ant-upload-drag-icon">
              <a-icon type="inbox" />
            </p>
            <p class="ant-upload-text">
              Кликните или перетащите сюда файл для загрузки
            </p>
          </a-upload-dragger>
          <a
            target="_blank"
            class="upload-file__link"
            v-show="params[column.name]"
            :href="params[column.name]"
          >
            {{ params[column.name] }}
          </a>
          <a-button
            block
            type="danger"
            v-show="params[column.name]"
            @click="params[column.name] = null"
          >
            Удалить файл
          </a-button>
        </div>
      </a-form-item>
    </a-form>
    <template slot="footer">
      <a-button @click="isVisible = false">Отменить</a-button>
      <a-button
        v-if="id && rights.delete"
        type="danger"
        :loading="isDeleting"
        @click="remove"
      >
        Удалить
      </a-button>
      <a-button
        v-if="columns.length"
        type="primary"
        :loading="isLoading"
        :disabled="!isValidated"
        @click="save"
      >
        Сохранить
      </a-button>
    </template>

    <a-modal width="90%" v-model="findEntity.isVisible">
      <div class="find-entity-selected" v-if="findEntity.multiple">
        Выбранные сущности:
        <a-tag
          v-for="id in findEntity.result"
          :key="id"
          closable
          @close="removeFoundEntity(id)"
        >
          {{ id }}
        </a-tag>
      </div>
      <a-badge class="controls__button">
        <a-button
          @click.prevent="$refs.filters.addFilter"
          type="primary"
          icon="filter"
        ></a-button>
      </a-badge>
      <filters
        v-if="isVisible"
        class="filters"
        :name="findEntity.entity"
        :forSearch="true"
        ref="filters"
        @change="filter"
        :visible="findEntity.isVisible"
      ></filters>
      <entity
        v-if="isVisible"
        ref="entity"
        :name="findEntity.entity"
        :forSearch="true"
        :visible="findEntity.isVisible"
        :selection="
          findEntity.multiple
            ? {
                selectedRowKeys: findEntity.result,
                onChange: (e) => (findEntity.result = e),
              }
            : null
        "
        @rowClick="clickFindEntity"
      />
      <template slot="footer">
        <a-button @click="findEntity.isVisible = false"> Отменить </a-button>
        <a-button
          v-if="findEntity.multiple"
          type="primary"
          @click="saveFindEntity"
        >
          Готово
        </a-button>
      </template>
    </a-modal>
  </a-modal>
</template>

<script>
import { debounce } from 'lodash';
import defs from '@/mixins/params';
import moment from 'moment';
import dateLocale from 'ant-design-vue/es/date-picker/locale/ru_RU';
import 'moment/locale/ru';
import Editor from '@tinymce/tinymce-vue';
import 'tinymce/plugins/link';
import 'tinymce/plugins/code';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/plugins/image';
import 'tinymce/plugins/media';
import 'tinymce/plugins/wordcount';
import Entity from '@/views/Entity/Content.vue';
import Filters from '@/components/Controls/Filters/index.vue';

export default {
  name: 'item-modal',
  mixins: [defs],
  components: { Editor, Entity, Filters },
  data() {
    return {
      findEntity: {
        isVisible: false,
        multiple: false,
        result: [],
        entity: '',
        param: '',
      },
      isDeleting: false,
      isValidated: false,
      params: {},
      processedColumns: [],
      dateLocale,
      formats: [
        {
          title: 'Headings',
          items: [
            { title: 'Heading 1', format: 'h1' },
            { title: 'Heading 2', format: 'h2' },
            { title: 'Heading 3', format: 'h3' },
          ],
        },
        {
          title: 'Стиль',
          items: [
            { title: 'Bold', format: 'bold' },
            { title: 'Italic', format: 'italic' },
            { title: 'Underline', format: 'underline' },
            { title: 'Strikethrough', format: 'strikethrough' },
            { title: 'Superscript', format: 'superscript' },
            { title: 'Subscript', format: 'subscript' },
          ],
        },
        {
          title: 'Align',
          items: [
            { title: 'Left', format: 'alignleft' },
            { title: 'Center', format: 'aligncenter' },
            { title: 'Right', format: 'alignright' },
          ],
        },
      ],
      debounced: {},
      requests: [],
    };
  },
  computed: {
    isLoading() {
      return this.requests.some(r => r && r.loading);
    },
    baseUrl() {
      return process.env.BASE_URL;
    },
    isVisible: {
      get() {
        return this.$store.getters.isRowModalVisible;
      },
      set(value) {
        this.$store.commit('setIsRowModalVisible', value);
      },
    },
    id() {
      return this.$store.getters.rowModalId;
    },
    sorting() {
      const obj = this.$store.getters.columnsSettings
        .find(v => v.entity === this.currentEntity) || {};
      return obj.sorted || [];
    },
    columns() {
      return Object.values(this.$store.getters.entities[this.currentEntity].params)
        .filter(v => v.name !== 'id' && v.name.indexOf('.') === -1 && !/_avg$/.test(v.name))
        .sort((a, b) => this.sorting.indexOf(a.name) - this.sorting.indexOf(b.name));
    },
  },
  methods: {
    saveFindEntity() {
      this.params[this.findEntity.param] = this.findEntity.result.join();
      this.findEntity.isVisible = false;
      this.validate();
    },
    removeFoundEntity(id) {
      const index = this.findEntity.result.indexOf(id);
      this.findEntity.result.splice(index, 1);
    },
    clickFindEntity(event) {
      if (this.findEntity.multiple) {
        return;
      }

      this.params[this.findEntity.param] = event;
      this.findEntity.isVisible = false;
      this.validate();
    },
    handleImage(param) {
      return (file) => {
        this.waitForRequest(param);
        this.$api.uploadFile({ file }).then((result) => {
          this.$refs[`${param}-image`][0].onload = () => {
            this.killRequest(param);
          };
          this.params[param] = '';
          this.$nextTick().then(() => {
            this.params[param] = result.data.file_url;
            this.validate();
          });
        }).catch(() => {
          this.killRequest(param);
        });
        return false;
      };
    },
    handleFile(column) {
      return (file) => {
        let restrictors = {};
        try {
          restrictors = JSON.parse(column.restrictor_params);
          // eslint-disable-next-line no-empty
        } catch {}

        const isValid = !restrictors.filetype
        || restrictors.filetype.replace(/\s/g, '').split(',')
          .some(type => new RegExp(`\\.${type}$`, 'g').test(file.name));

        if (!isValid) {
          this.$notification.error({
            message: 'Ошибка',
            description: `Разрешенные ограничителем типы файлов: ${restrictors.filetype}`,
          });
          return false;
        }

        const param = column.name;
        this.waitForRequest(param);
        this.$api.uploadFile({ file }).then((result) => {
          this.params[param] = result.data.file_url;
          this.validate();
          this.killRequest(param);
        }).catch(() => {
          this.killRequest(param);
        });
        return false;
      };
    },
    filter(e) {
      if (this.$refs.entity) {
        this.$refs.entity.handleFilter(e);
        return;
      }
      this.$nextTick(this.$refs.entity.handleFilter(e));
    },
    getRadioOptions(column) {
      const arr = this.getCheckboxOptions(column);
      arr.unshift({ label: 'Не выбрано', value: '' });
      return arr;
    },
    getCheckboxOptions(column) {
      const str = (column.restrictors.find(v => v.name === 'contains') || {}).params || '';
      return str ? str.split('||') : [];
    },
    getMoment(timestamp) {
      return moment.unix(timestamp);
    },
    handleDatepicker(event, column) {
      this.params[column] = event ? event.unix() : '';
      this.validate();
    },
    open() {
      this.isVisible = true;
      this.params = {};
    },
    validate() {
      this.processedColumns.map((column) => {
        if (!column.restrictors) {
          return column;
        }

        const isRequired = column.restrictors.some(r => r.name === 'required');

        column.restrictors.forEach((r) => {
          let val = this.params[column.name] || '';
          val += '';
          if (!isRequired && !val.length) {
            // eslint-disable-next-line
            r.validated = true;
            return;
          }

          if (r.name === 'required') {
            // eslint-disable-next-line
            r.validated = !!(val && val.length);
          } else if (r.name === 'email') {
            // eslint-disable-next-line
            r.validated = /\S+@\S+\.\S+/.test(val);
          } else if (r.name === 'link') {
            // eslint-disable-next-line
            r.validated = /(?:(?:https?:\/\/)|(?:www\.))[^\s]+/.test(val);
          } else if (r.name === 'number') {
            // eslint-disable-next-line
            r.validated = val.length && !isNaN(+val);
          } else if (r.name === 'decimalNumber') {
            // eslint-disable-next-line
            r.validated = val.length && !isNaN(+val) && (val.split('.')[1] || '').length == r.params;
          } else if (r.name === 'boolean') {
            // eslint-disable-next-line
            r.validated = val == 1 || val == 0;
          } else if (r.name === 'contains') {
            let separator = '||';
            if (column.decorator_name === 'checkbox') {
              separator = ', ';
            }
            // eslint-disable-next-line
            r.validated = val.split(separator).every(v => r.params.split('||').includes(v));
          } else if (r.name === 'greater') {
            // eslint-disable-next-line
            r.validated = val.length && +val > r.params;
          } else if (r.name === 'less') {
            // eslint-disable-next-line
            r.validated = val.length && +val < r.params;
          } else if (r.name === 'lengthGreater') {
            // eslint-disable-next-line
            r.validated = val.length && val.length > r.params;
          } else if (r.name === 'lengthLess') {
            // eslint-disable-next-line
            r.validated = val.length && val.length < r.params;
          } else if (r.name === 'entity') {
            const paramArray = column.name.split('_');
            if (paramArray[1] !== 'id') {
              // eslint-disable-next-line
              r.validated = false;
              return;
            }
            this.waitForRequest(`validate-id-${column.name}`);
            this.debounced[column.name].validateIds(paramArray[0], val, r, column.name);
          } else if (r.name === 'entities') {
            const paramArray = column.name.split('_');
            if (paramArray[1] !== 'ids') {
              // eslint-disable-next-line
              r.validated = false;
              return;
            }
            this.waitForRequest(`validate-ids-${column.name}`);
            this.debounced[column.name].validateIds(paramArray[0], val, r, column.name);
          } else if (r.name === 'unique') {
            if (!r.params) {
              // eslint-disable-next-line
              r.validated = true;
              return;
            }
            const cols = r.params.split(',');
            this.waitForRequest(`validate-u-${column.name}`);
            this.debounced[column.name].validateUniqueness(cols, val, r, column.name);
          }
        });

        return column;
      });

      this.isValidated = this.processedColumns
        .every(column => column.restrictors.every(r => r.validated));
    },
    validateIds(entity, val, r, column) {
      if (!this.isVisible || !val) {
        this.killRequest(`validate-id-${column}`);
        return;
      }
      const idsCount = val.split(',').length;
      this.$api.search({
        entity,
        start: 0,
        length: idsCount,
        params: ['id'],
        filter: JSON.stringify([{ 'id IN': val }]),
      }, `validate-id-${column}`).then((response) => {
        // eslint-disable-next-line
              r.validated = !!(response.data && idsCount === response.data.length);
        this.isValidated = this.processedColumns
          .every(col => col.restrictors.every(res => res.validated));
      }).catch(() => {
      }).finally(() => {
        this.killRequest(`validate-id-${column}`);
        this.killRequest(`validate-ids-${column}`);
      });
    },
    validateUniqueness(cols, val, r, column) {
      if (!this.isVisible || !val) {
        this.killRequest(`validate-u-${column}`);
        return;
      }
      let filter = [];
      cols.forEach((c) => {
        if (!this.params[c]) {
          filter.push({ [`AND ${c} IS`]: 'NULL' });
          return;
        }
        filter.push({ [`AND ${c} =`]: this.params[c] });
      });
      if (this.id) {
        filter = [{ '': [{ 'id !=': this.id }, { AND: filter }] }];
      }
      this.$api.search({
        entity: this.currentEntity,
        start: 0,
        length: 1,
        params: ['id', ...cols],
        filter: JSON.stringify(filter),
      }, `validate-uniqueness-${column}`).then((response) => {
        // eslint-disable-next-line
        r.validated = !(response.data && response.data.length);
        this.isValidated = this.processedColumns
          .every(col => col.restrictors.every(res => res.validated));
        this.$forceUpdate();
      }).catch(() => {
      }).finally(() => {
        this.killRequest(`validate-u-${column}`);
      });
    },
    save() {
      this.waitForRequest('save');

      if (!this.isValidated) {
        this.killRequest('save');
        return;
      }

      this.$api.editEntity({
        name: this.currentEntity,
        id: this.id,
        params: this.params,
      }).then(() => {
        this.isVisible = false;
        this.$store.commit('updateEntityContent');
      }).finally(() => {
        this.killRequest('save');
      });
    },
    remove() {
      // eslint-disable-next-line
      if (!confirm(`Вы действительно хотите удалить строку?`)) {
        return;
      }
      this.isDeleting = true;
      this.$api.deleteEntities({
        name: this.currentEntity,
        filter: JSON.stringify([{ 'id =': this.id }]),
      }).then(() => {
        this.$store.commit('updateEntityContent');
        this.isVisible = false;
      }).finally(() => {
        this.isDeleting = false;
      });
    },
    processColumns() {
      this.debounced = {};
      this.processedColumns = this.columns.map((column) => {
        this.debounced[column.name] = {
          validateIds: debounce(this.validateIds, 500),
          validateUniqueness: debounce(this.validateUniqueness, 500),
        };
        // eslint-disable-next-line
        column.restrictors = [];
        if (column.restrictor_names) {
          try {
            JSON.parse(column.restrictor_names).forEach((r) => {
              const config = {
                name: r,
                label: this.restrictors[r].label,
                params: JSON.parse(column.restrictor_params)[r] || '',
                validated: false,
              };

              if (r === 'decimalNumber') {
                config.label += ` (${config.params})`;
              }

              column.restrictors.push(config);
            });
          } catch {
            // eslint-disable-no-empty
          }
        }

        return column;
      });
      this.validate();
    },
    isRequestPending(name) {
      return (this.requests.find(r => r.name === name) || {}).loading;
    },
    waitForRequest(name) {
      const request = this.requests.find(r => r.name === name);
      if (request) {
        request.loading = true;
      } else {
        this.requests.push({ name, loading: true });
      }
    },
    killRequest(name) {
      const request = this.requests.find(r => r.name === name);
      if (request) {
        request.loading = false;
      }
    },
    getRestrictorParams(restrictor) {
      if (restrictor.name === 'unique') {
        const { params } = this.$store.getters.entities[this.currentEntity];
        return restrictor.params
          .split(',')
          .map(c => params[c].gui_name || params[c].name)
          .join(', ');
      }

      if (restrictor.name === 'decimalNumber') {
        return '';
      }

      return restrictor.params || '';
    },
  },
  watch: {
    columns() {
      this.processColumns();
    },
    isVisible(val) {
      this.requests = [];
      if (!val) {
        this.$store.commit('setRowModalId', null);
      } else if (this.id) {
        this.params = {};
        this.waitForRequest('modal-data');
        this.$api.search({
          entity: this.currentEntity,
          start: 0,
          length: 10,
          params: ['id', ...this.columns.map(c => c.name)],
          filter: JSON.stringify([{ 'id =': this.id }]),
        }, 'get-edit-data').then((response) => {
          const params = response.data[0];
          delete params.id;
          this.params = Object.fromEntries(Object.entries(params).filter(entry => !/^\w*\.\w*$/.test(entry[0])));
          this.validate();
        }).finally(() => {
          this.killRequest('modal-data');
        });
      } else {
        this.params = Object.fromEntries(this.columns.map(col => [col.name, null]));
      }

      this.processColumns();
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-image,
.upload-file {
  &__preview {
    max-width: 100%;
  }

  &__link {
    display: block;
    line-height: 1.2;
    margin: 7px 0 2px;
    word-break: break-all;
  }

  &::v-deep {
    .upload-image__loader,
    .upload-file__loader {
      display: none;
    }
  }

  &_pending {
    &::v-deep {
      pointer-events: none;

      .upload-image__loader,
      .upload-file__loader {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
      }

      .ant-upload-drag-icon,
      .ant-upload-text {
        opacity: 0;
      }
    }
  }
}

.form-item {
  margin-bottom: 5px;
}

.restrictors {
  list-style: inside decimal;
}

.icon_red,
.li_red {
  color: red;
}

.datepicker {
  width: 100%;
}

.tinymce {
  font-family: "Roboto", sans-serif !important;
}

.find-entity {
  display: flex;

  &__button {
    margin-left: 5px;
  }
}

.controls__button,
.filters {
  margin-bottom: 10px;
}

.modal {
  width: 80%;
}

.find-entity-selected {
  margin-bottom: 15px;
}
</style>

<style lang="scss">
.row-modal {
  .ant-form-item-label {
    line-height: 20px;
  }
}
</style>
